import React, { useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, arrayMove } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import SortableContext from "src/components/Sortable/Context";
import { verticalListSortingStrategy, horizontalListSortingStrategy } from "src/components/Sortable/Container/strategy";
import { v4 } from "uuid";
import SortableItem from "src/components/Sortable/Item";
import ContainerWithoutContext from "src/components/Sortable/ContainerWithoutContext";
import { standardErrorMessages } from "src/utils/errors";
import DragOverlay from "src/components/Sortable/DragOverlay";
import { createPortal } from "react-dom";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import FiltersContainer from "src/components/FiltersContainer";
import IFiltersActionsPanel, { ActionSituation, IActionShowPostponementRef, IRefActionsPanelModalAddChangeDelete, IRefActionsProgressModal } from "src/pages/Actions/Panel/interface";
import Select from "src/components/Select";
import LovIndicators from "src/components/Lov/Indicators";
import LovObjectives from "src/components/Lov/Objectives";
import LovPerspectives from "src/components/Lov/Perspectives";
import LovMaps from "src/components/Lov/Maps";
import LovSectors from "src/components/Lov/Sectors";
import LovUsers from "src/components/Lov/Users";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import ModalProgress from "src/pages/Actions/Panel/components/modalProgress";
import formatDate from "src/utils/formatDate";
import ModalsSubactions, { IRefModalSubaction } from "src/pages/Actions/Panel/components/modalsSubactions";
import ModalCancel, { IActionCancelRef } from "src/pages/Actions/Panel/components/modalCancel";
import CommentsModal, { IActionCommentsRef } from "src/pages/Actions/Panel/components/modalComments";
import ModalHistory, { IActionHistoryRef } from "src/pages/Actions/Panel/components/modalHistory";
import ModalPostponement, { IActionPostponementRef } from "src/pages/Actions/Panel/components/modalPostponement";
import ModalAddChangeDelete from "src/pages/Actions/Panel/components/modalAddChangeDelete";
import ModalEfficiency, { IActionEfficiencyRef } from "src/pages/Actions/Panel/components/modalEfficiency";
import LovProject from "src/components/Lov/Projects";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import Loader from "src/components/Loader";
import ModalShowPostponements from "src/pages/Actions/Panel/components/modalShowPostponements";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import stringToDate from "src/utils/stringToDate";
import DroppableContainer from "../../components/DroppableContainer";
import PlayingCard from "../../components/Card";
import { RowKanban } from "../MultipleKanbans/interface";
import { WrapContext } from "../MultipleKanbans/styles";

const DefaultKanban: React.FC = () => {

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate } = useTranslation();

	const {addToast} = RCCHookToast();
	const { checkIfHasSpecificPermission, administrator, superUser } = useAuth();

	const [items, setItems] = useState<Record<string, RowKanban[]>>({});
	const [tempItems, setTempItems] = useState<Record<string, RowKanban[]>>({});

	const modalAddChangeDelete = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const modalProgressRef = useRef<IRefActionsProgressModal>(null);
	const modalsSubactionsRef = useRef<IRefModalSubaction>(null);
	const modalCommentsRef = useRef<IActionCommentsRef>(null);
	const modalHistoryRef = useRef<IActionHistoryRef>(null);
	const modalPostponementRef = useRef<IActionPostponementRef>(null);
	const modalCancelRef = useRef<IActionCancelRef>(null);
	const modalEfficiencyRef = useRef<IActionEfficiencyRef>(null);
	const modalShowPostponements = useRef<IActionShowPostponementRef>(null);

	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFiltersActionsPanel>({ 
		f: objQueryParams?.f || "", 
		responsible: objQueryParams?.responsible || "",
		authority: objQueryParams?.authority || "",
		consulted: objQueryParams?.consulted || "",
		informed: objQueryParams?.informed || "",
		sector: objQueryParams?.sector || "",
		situation: objQueryParams?.situation || [{
			label: translate("Concluída"),
			value: ActionSituation.FINISHED
		},{
			label: translate("Em andamento"),
			value: ActionSituation.IN_PROGRESS
		},{
			label: translate("Não Iniciada"),
			value: ActionSituation.NOT_STARTED
		}],
		startDate: objQueryParams?.startDate ? stringToDate(objQueryParams.startDate) : null,
		endDate: objQueryParams?.endDate ? stringToDate(objQueryParams.endDate) : null,
		tags: objQueryParams?.tags || "",
		map: objQueryParams?.map || "",
		perspective: objQueryParams?.perspective || "",
		objective: objQueryParams?.objective || "",
		indicator: objQueryParams?.indicator || "",
		filterOrigin: objQueryParams?.filterOrigin || "",
		project: objQueryParams?.project || "",
		percConclusion: [0, 100]
	});

	const [activeId, setActiveId] = useState<string | null>(null);
	const [activeContainerDrag, setActiveContainerDrag] = useState<string | null | undefined>(null);

	const [waitingForResponse, setWaitingForResponse] = useState(false);
	const { handleSubmit : handleSubmitFilters, register : registerFilters, watch: watchFilters, control: controlFilters, reset: resetFilters, setValue: setValueFilters } = useForm();

	const mapValueWatch = watchFilters("map");
	const perspectiveValueWatch = watchFilters("perspective");

	const nameColumnsDefaultKanban = ["coluna-1", "coluna-2", "coluna-3"];

	const handleDragStart = (activeIdX: string) => {
		setActiveContainerDrag(findContainer(activeIdX));
		setActiveId(activeIdX);
		setTempItems(items);
	};	


	const handleDragOver = (activeIdX: string, overId: string) => {		
		const overContainer = findContainer(overId);
		const activeContainer = findContainer(activeIdX);

		if (!overContainer || !activeContainer) {
			return;
		}

		if(activeContainerDrag)
			if(overContainer < activeContainerDrag)
				return;

		if (activeContainer !== overContainer) {
			setItems((itemsX) => {
				const activeItems = itemsX[activeContainer];
				const overItems = itemsX[overContainer];
				const overIndex = overItems.map(({ id }) => id).indexOf(overId);
				const activeIndex = activeItems.map(({ id }) => id).indexOf(activeIdX);

				let newIndex: number;

				if (overId in items) {
				 	newIndex = overItems.length + 1;
				}else{
					newIndex = overIndex >= 0 ? overIndex : overItems.length + 1;
				}
				return {
					...itemsX,
					[activeContainer]: itemsX[activeContainer].filter(
						(item) => String(item.id) !== activeIdX
					),
					[overContainer]: [
						...itemsX[overContainer].slice(0, newIndex),
						itemsX[activeContainer][activeIndex],
						...itemsX[overContainer].slice(
							newIndex,
							itemsX[overContainer].length
						)
					]
				};
			});
		}

	};

	const handleDragEnd = async (activeIdX: string, overId: string) => {
		
		const activeContainer = findContainer(activeIdX);

		if (!activeContainer) {
			setActiveId(null);
			return;
		}

		if (!overId) {
			setActiveId(null);
			return;
		}

		const overContainer = findContainer(overId);

		if(overContainer && activeContainerDrag)
			if(overContainer < activeContainer)
				return;

		if (overContainer) {
			const activeIndex = items[activeContainer].map(({ id }) => id).indexOf(activeIdX);
			const overIndex = items[overContainer].map(({ id }) => id).indexOf(overId);

			setItems((itemsX) => ({
				...itemsX,
				[overContainer]: arrayMove(
					itemsX[overContainer],
					activeIndex,
					overIndex
				)
			}));

			if(activeContainer !== activeContainerDrag){
				const res = await changeActionStatus(activeIdX, activeContainer);
				if(!res){
					setItems(tempItems);
				}
			}
		}
		setActiveId(null);
	};

	const submitFilters = async (data: any) => {
		const dataForm = data;
		if(data.situation)
			dataForm.situation = extractSingleValueFromArrayOfObject("value", data.situation)?.toString();
		
		if(typeof data.authority === "object")
			dataForm.authority = data.authority?.value || "";
		if(typeof data.consulted === "object")
			dataForm.consulted = data.consulted?.value || "";
		if(typeof data.informed === "object")
			dataForm.informed = data.informed?.value || "";
		if(typeof data.responsible === "object")
			dataForm.responsible = data.responsible?.value || "";
		if(typeof data.sector === "object")
			dataForm.sector = data.sector?.value || "";
		if(typeof data.map === "object")
			dataForm.map = data.map?.value || "";
		if(typeof data.perspective === "object")
			dataForm.perspective = data.perspective?.value ;
		if(typeof data.objective === "object")
			dataForm.objective = data.objective?.value || "";
		if(typeof data.indicator === "object")
			dataForm.indicator = data.indicator?.value || "";
		if(typeof data.project === "object")
			dataForm.project = data.project?.value || "";
		dataForm.filterOrigin = "";

		setFilters(dataForm);
	};

	const changeActionStatus = (actionId: string, containerName: string) => {
		setWaitingForResponse(true);

		const item = items[containerName].find(({ id }) => id === actionId);
		if(!item)
			return false;

		let isFinishing = false;
		let percentage = item.inb001PercConclusao;

		const containerId = containerName.split("-")[1];

		if(containerId === "3"){
			isFinishing = true;
			percentage = 100;
		}

		modalProgressRef?.current?.openModalChangeProgress(item.inb001Id, `${item.inb001Codigo} - ${item.inb001Titulo}`, percentage , () => {
			loadData();
			setWaitingForResponse(false);
		}, false, () => { 
			setItems(tempItems);	
			setWaitingForResponse(false);
		 }, isFinishing);

	
		return true;
	};


	const renderSortableItemDragOverlay = (row: RowKanban | undefined) => (
		<PlayingCard 
			key={row?.id}
			value={row || {} as RowKanban} 													
	 />
	);

	const findContainer = (idX: string) => {
		if(items){
			if (idX in items) {
				return idX;
			}
			return Object.keys(items).find((key) => items[key].map(({ id }) => id).find((key2) => key2.includes(idX)));
		}	
		return null;	
	};

	const DragOverlayRender = (idActive: string) => {
		if(idActive){
			const container = findContainer(idActive);
			let item;

			if(container && items)
				item = items[container].find((itemX) => itemX.id === idActive);

			return renderSortableItemDragOverlay(item);
		}
		return null;
	};

	const optionsSituation = [{
		label: translate("Cancelada"),
		value: ActionSituation.CANCELED
	},{
		label: translate("Em Rascunho"),
		value: ActionSituation.DRAFT
	},{
		label: translate("Concluída"),
		value: ActionSituation.FINISHED
	},{
		label: translate("Em andamento"),
		value: ActionSituation.IN_PROGRESS
	},{
		label: translate("Não Iniciada"),
		value: ActionSituation.NOT_STARTED
	}];

	const { errorFetching, loading, loadData } = useEffectFetchData<{ 
		dataColumns : Record<string, RowKanban[]> 
	}> (
		"Kanban/ListDefaultKanban", 
		{ 
			f: filters.f || "",
			responsible: filters.responsible || "",
			authority: filters.authority || "",
			consulted: filters.consulted || "",
			informed: filters.informed || "",
			sector: filters.sector || "",
			situation: filters.situation ? extractSingleValueFromArrayOfObject("value", filters.situation)?.toString() : "",
			startDate: filters.startDate ? formatDate(filters.startDate) : "",
			endDate: filters.endDate ? formatDate(filters.endDate) : "",
			tags: filters.tags || "",
			map: filters.map || "",
			perspective: filters.perspective || "",
			objective: filters.objective || "",
			indicator: filters.indicator || "",
			project: filters.project || "",
			filterOrigin: filters.filterOrigin || ""
		 },
		 ({ data }) => {
			setItems(data.dataColumns);
		 },
		 [filters]
	);

	const onCallbackDeleteAction = (idNumber: number) => {
		const id = idNumber.toString();

		const actualContaiener = findContainer(id);
		if(!actualContaiener)
			return;


		const objects = items[actualContaiener].filter((item: any) => item.inb001Id !== idNumber);

		setItems((itemsX) => ({
			...itemsX,
			[actualContaiener]: objects
		}));
	};

	const callbackUpdateAction = async (id: string) => {
		try{
			const response = await RCCServicesApi.getRequest({
				method: "Actions/InfosOfActionForList",
				urlParameters: {
					id
				}
			});
			const action = response.data as RowKanban;

			const actualContainer = findContainer(String(id));
			if(!actualContainer)
				return;

			const actualItem = items[actualContainer].find((item: any) => item.id === String(id));
			if(!actualItem)
				return;

			if(action.situation === actualItem.situation && activeId){
				setItems(tempItems);
				return;
			}

			const actualItemIndex = items[actualContainer].findIndex((item: any) => item.id === String(id));
			if(actualItemIndex === -1 )
				return;

			const objects = [...items[actualContainer]];

			objects[actualItemIndex] = {...action, id : action.inb001Id.toString() };

			setItems((itemsX) => ({
				...itemsX,
				[actualContainer]: objects
			}));

		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar ação, favor atualizar a pagina"), addToast);
		}
	};


	return (
		<>
			<RCCGrid 
				container 
				item 
				justifyContent="flex-end"
			>
				<RCCButton
					icon={{name: "filter", color: "#FFF", size: 16}}
					color={showFilters ||
								filters.authority ||
								filters.consulted || 
								filters.endDate || 
								filters.f || 
								filters.informed ||
								filters.responsible || 
								filters.sector || 
								filters.situation || 
								filters.project || 
								filters.startDate
							 ? "blue" : "grey"}
					layout="isRounded"
					size="md"
					onClick={() => setShowFilters((prevState) => !prevState)}
				/>
			</RCCGrid>
			
			{/* Filters */}
			<FiltersContainer
				showFilters={showFilters}
				onSubmitFilters={handleSubmitFilters(submitFilters)}
				resetFilters={() => resetFilters({ 
					f: "", 
					responsible: { value: "", label: ""},
					authority: { value: "", label: ""},
					consulted: { value: "", label: ""},
					informed: { value: "", label: ""},
					sector: { value: "", label: ""},
					situation: [{
						label: translate("Concluída"),
						value: ActionSituation.FINISHED
					},{
						label: translate("Em andamento"),
						value: ActionSituation.IN_PROGRESS
					},{
						label: translate("Não Iniciada"),
						value: ActionSituation.NOT_STARTED
					}],
					startDate: null,
					endDate: null,
					map: "",
					perspective: "",
					objective: "",
					indicator: { value: "", label: ""},
					project:  { value: "", label: ""},
					percConclusion: [0, 100]
				})}
			>
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<DateTimePickerRange
							control={controlFilters}
							label={translate("Período")}
							labelSeparator={translate("até")}
							nameInit = "startDate"
							nameEnd = "endDate"
							defaultValueEnd={filters.endDate}
							defaultValueInit={filters.startDate}
							type="datePicker"
							setValue={setValueFilters}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovUsers 
							control={controlFilters}
							name="authority"
							label={translate("Autoridade")}
							defaultValue={filters.authority}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovUsers 
							control={controlFilters}
							name="responsible"
							label={translate("Responsável")}
							defaultValue={filters.responsible}
							isClearable
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovUsers 
							control={controlFilters}
							name="consulted"
							label={translate("Consultado")}
							defaultValue={filters.consulted}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovUsers 
							control={controlFilters}
							name="informed"
							label={translate("Informado")}
							defaultValue={filters.informed}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovSectors 
							control={controlFilters}
							name="sector"
							label={translate("Setor")}
							defaultValue={filters.sector}
							isClearable
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovMaps 
							control={controlFilters}
							name="map"
							label={translate("Mapas")}
							defaultValue={filters.map || undefined}
							onChangeCapture={() => {
								setValueFilters("perspective" , undefined);
								setValueFilters("objective" , undefined);
							}}
							submit="value and label"
							cacheOptions={false}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovPerspectives 
							  key={mapValueWatch?.value || mapValueWatch || v4()}
							control={controlFilters}
							name="perspective"
							label={translate("Perspectivas")}
							defaultValue={filters.perspective}
							extraParametersOnLoad={{
								map: mapValueWatch?.value || mapValueWatch || ""
							}}
							onChangeCapture={() => {
								setValueFilters("objective" , undefined);
							}}
							submit="value and label"
							disabled={!mapValueWatch}
							cacheOptions={false}
							isClearable
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovObjectives 
							key={perspectiveValueWatch?.value || perspectiveValueWatch || v4()}
							control={controlFilters}
							name="objective"
							label={translate("Objetivo")}
							defaultValue={filters.objective}
							extraParametersOnLoad={{
								map: mapValueWatch?.value || mapValueWatch || "",
								perspective: perspectiveValueWatch?.value || perspectiveValueWatch || ""
							}}
							submit="value and label"
							disabled={!mapValueWatch || !perspectiveValueWatch}
							cacheOptions={false}
							isClearable
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovIndicators 
							control={controlFilters}
							name="indicator"
							label={translate("Indicadores")}
							defaultValue={filters.indicator}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<Select 
							control={controlFilters}
							name="situation"
							label={translate("Situação")}
							defaultValue={filters.situation || [{
								label: translate("Concluída"),
								value: ActionSituation.FINISHED
							},{
								label: translate("Em andamento"),
								value: ActionSituation.IN_PROGRESS
							},{
								label: translate("Não Iniciada"),
								value: ActionSituation.NOT_STARTED
							}]}
							options={optionsSituation}
							isMulti
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<LovProject 
							name="project"
							control={controlFilters}
							label={translate("Projeto")}	
							defaultValue={filters.project || ""}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={4}
						lg={4}
					>
						<RCCInput
							name="f" 
							label={translate("Pesquisar")}
							register={registerFilters}
							defaultValue={filters.f}
						/>
					</RCCGrid>
				</RCCGrid>            
			</FiltersContainer>

			{errorFetching ? (
				<RCCAlert 
					color="red"
					title={translate("Atenção")}
					description={translate("Erro ao carregar dados")}
				/>
			): 
				<>
					{loading ? 
						<Loader /> : 
						<RCCGrid container item>
							<SortableContext 
								onDragStart={handleDragStart}
								onDragOver={handleDragOver}
								onDragEnd={handleDragEnd}
								executeOnDragEndWhenActiveAndOverEquals
							>
		
								<WrapContext>
									<ContainerWithoutContext
										items={[...nameColumnsDefaultKanban]}
										strategy={horizontalListSortingStrategy}
									>
		
										<RCCGrid
											container
											item
											direction="row"
											wrap="nowrap"
											style={{ height: "100%", width: "100%", overflowY: "hidden", margin: "0 auto",overflowX: "auto" }}
										>
											<DroppableContainer 
												key={nameColumnsDefaultKanban[0]}
												idColumn={nameColumnsDefaultKanban[0]}
												numberOfItems={items[nameColumnsDefaultKanban[0]]?.length ?? 0}
												columnsData={{
													kba002Cor: "#393e86",
													kba002Descricao: translate("Não iniciado"),
													kba002Id: 0,
													kba002Ordem: 0
												}}
												isDefaultColumn
											>
												<ContainerWithoutContext key={nameColumnsDefaultKanban[0]} items={items[nameColumnsDefaultKanban[0]] ?? []} strategy={verticalListSortingStrategy}>
													<InfiniteScrollWithoutFetch 
														defaultNumberOfElements={7}
														manuallyToggleShowMore
														listElements={items[nameColumnsDefaultKanban[0]] ?? []}
														mapper={(valueItem) => (
															<React.Fragment key={v4()}>
																{valueItem && valueItem.id && (
																	<SortableItem 
																		id={valueItem.id}
																		disabled={waitingForResponse || (!(superUser || administrator) && !checkIfHasSpecificPermission("actions/kanban", PermissionEnum.CREATE_UPDATE))}
																	>
																		<PlayingCard 
																			value={valueItem}		
																			modalCommentsRef={modalCommentsRef}
																			modalCancelRef={modalCancelRef}
																			modalsSubactionsRef={modalsSubactionsRef}
																			modalPostponementRef={modalPostponementRef}
																			modalsRef={modalAddChangeDelete}
																			modalEfficiencyRef={modalEfficiencyRef}
																			modalProgressRef={modalProgressRef}
																			modalShowPostponementsRef={modalShowPostponements}
																			onCallbackUpdateFunction={callbackUpdateAction}
																			modalHistoryRef={modalHistoryRef} 		
																			hideSortableHandle={(!(superUser || administrator) && !checkIfHasSpecificPermission("actions/kanban", PermissionEnum.CREATE_UPDATE))}									
																		/>	
																	</SortableItem>
																)}
															</React.Fragment>
														)}
													/>		
												</ContainerWithoutContext>	
											</DroppableContainer>
								
											<DroppableContainer 
												key={nameColumnsDefaultKanban[1]}
												idColumn={nameColumnsDefaultKanban[1]}
												numberOfItems={items[nameColumnsDefaultKanban[1]]?.length ?? 0}
												columnsData={{
													kba002Cor: "#007bbc",
													kba002Descricao: translate("Em andamento"),
													kba002Id: 1,
													kba002Ordem: 1
												}}
												isDefaultColumn
											>
												<ContainerWithoutContext key={nameColumnsDefaultKanban[1]} items={items[nameColumnsDefaultKanban[1]] ?? []} strategy={verticalListSortingStrategy}>
													<InfiniteScrollWithoutFetch 
														defaultNumberOfElements={7}
														manuallyToggleShowMore
														listElements={items[nameColumnsDefaultKanban[1]] ?? []}
														mapper={(valueItem) => (
															<React.Fragment key={v4()}>
																{valueItem && valueItem.id && (
																	<SortableItem 
																		id={valueItem.id}
																		disabled={waitingForResponse || (!(superUser || administrator) && !checkIfHasSpecificPermission("actions/kanban", PermissionEnum.CREATE_UPDATE))}
																	>
																		<PlayingCard 
																			value={valueItem}			
																			modalCommentsRef={modalCommentsRef}
																			modalCancelRef={modalCancelRef}
																			modalsSubactionsRef={modalsSubactionsRef}
																			modalPostponementRef={modalPostponementRef}
																			modalsRef={modalAddChangeDelete}
																			modalEfficiencyRef={modalEfficiencyRef}
																			modalShowPostponementsRef={modalShowPostponements}
																			modalProgressRef={modalProgressRef}
																			onCallbackUpdateFunction={callbackUpdateAction}
																			modalHistoryRef={modalHistoryRef} 				
																			hideSortableHandle={(!(superUser || administrator) && !checkIfHasSpecificPermission("actions/kanban", PermissionEnum.CREATE_UPDATE))}						
																		/>	
																	</SortableItem>
																)}
															</React.Fragment>
														)}
													/>		
												</ContainerWithoutContext>	
											</DroppableContainer>
								
											<DroppableContainer 
												key={nameColumnsDefaultKanban[2]}
												idColumn={nameColumnsDefaultKanban[2]}
												numberOfItems={items[nameColumnsDefaultKanban[2]]?.length ?? 0}
												columnsData={{
													kba002Cor: "#00b38e",
													kba002Descricao: translate("Concluído"),
													kba002Id: 1,
													kba002Ordem: 1
												}}
												isDefaultColumn
											>
												<ContainerWithoutContext key={nameColumnsDefaultKanban[2]} items={items[nameColumnsDefaultKanban[2]] ?? []} strategy={verticalListSortingStrategy}>
													<InfiniteScrollWithoutFetch 
														defaultNumberOfElements={7}
														manuallyToggleShowMore
														listElements={items[nameColumnsDefaultKanban[2]] ?? []}
														mapper={(valueItem) => (
															<React.Fragment key={v4()}>
																{valueItem && valueItem.id && (
																	<SortableItem 
																		id={valueItem.id}
																		disabled={waitingForResponse || (!(superUser || administrator) && !checkIfHasSpecificPermission("actions/kanban", PermissionEnum.CREATE_UPDATE))}
																	>
																		<PlayingCard 
																			value={valueItem}							
																			modalCommentsRef={modalCommentsRef}
																			modalCancelRef={modalCancelRef}
																			modalsSubactionsRef={modalsSubactionsRef}
																			modalPostponementRef={modalPostponementRef}
																			modalsRef={modalAddChangeDelete}
																			modalEfficiencyRef={modalEfficiencyRef}
																			modalShowPostponementsRef={modalShowPostponements}
																			modalProgressRef={modalProgressRef}
																			onCallbackUpdateFunction={callbackUpdateAction}
																			modalHistoryRef={modalHistoryRef} 			
																			hideSortableHandle={(!(superUser || administrator) && !checkIfHasSpecificPermission("actions/kanban", PermissionEnum.CREATE_UPDATE))}			
																		/>	
																	</SortableItem>
																)}
															</React.Fragment>
														)}
													/>		
												</ContainerWithoutContext>	
											</DroppableContainer>
		
										</RCCGrid>
		
										{createPortal(
											<DragOverlay>
												{activeId ? DragOverlayRender(activeId) : null}
											</DragOverlay>,
											document.body
										)}
									</ContainerWithoutContext>
							
		
								</WrapContext>
						
							</SortableContext>
						</RCCGrid>
					}
				</>
			}

			<ModalProgress ref={modalProgressRef} />

			<ModalsSubactions
				ref={modalsSubactionsRef} 
				onCallbackClose={() => {}}
			/>

			<ModalAddChangeDelete 
				ref={modalAddChangeDelete} 
				onCallbackDelete={onCallbackDeleteAction}
			/>

			<CommentsModal ref={modalCommentsRef} />

			<ModalHistory ref={modalHistoryRef} />

			<ModalProgress ref={modalProgressRef} />

			<ModalPostponement ref={modalPostponementRef} />

			<ModalCancel ref={modalCancelRef} />

			<ModalEfficiency ref={modalEfficiencyRef} />

			<ModalShowPostponements ref={modalShowPostponements} />

		</>
	);
};

export default DefaultKanban;
